/**
 * Helpers compartidos para exportar inscripciones de eventos a Excel
 * incluyendo datos personales (documento, fecha de nacimiento, edad).
 */
import type { Person, EventRegistration } from "@/types";
import { ID_DOCUMENT_TYPE_LABELS } from "@/types";
import type { XlsxColumn } from "@/lib/utils/exportXlsx";

export function buildPersonsMap(persons: Person[] | undefined | null) {
  const map = new Map<string, Person>();
  if (!persons) return map;
  for (const p of persons) {
    if (p?.id) map.set(p.id, p);
  }
  return map;
}

function toDate(
  ts: { toDate?: () => Date } | Date | null | undefined,
): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts.toDate === "function") {
    try {
      return ts.toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function computeAge(birthDate: Date | null): number | "" {
  if (!birthDate) return "";
  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const m = now.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birthDate.getDate())) age--;
  return age >= 0 ? age : "";
}

/**
 * Columnas estándar con datos de la persona inscrita. Requiere un map
 * de personas por id (usar `buildPersonsMap(usePersons().data)`).
 */
export function personColumns(
  personsMap: Map<string, Person>,
): XlsxColumn<EventRegistration>[] {
  return [
    {
      header: "Tipo Documento",
      key: (r) => {
        const p = personsMap.get(r.personId);
        if (!p?.idDocumentType) return "";
        return ID_DOCUMENT_TYPE_LABELS[p.idDocumentType] || p.idDocumentType;
      },
      width: 18,
    },
    {
      header: "N° Documento",
      key: (r) => personsMap.get(r.personId)?.idDocumentNumber ?? "",
      width: 16,
    },
    {
      header: "Fecha Nacimiento",
      key: (r) => {
        const d = toDate(personsMap.get(r.personId)?.birthDate);
        return d ? d.toLocaleDateString("es-CO") : "";
      },
      width: 14,
    },
    {
      header: "Edad",
      key: (r) => computeAge(toDate(personsMap.get(r.personId)?.birthDate)),
      width: 8,
    },
  ];
}
